import React, {Component} from "react"
import {
  ScatterChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Scatter,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  LabelList,
  ScatterProps
} from "recharts"
import PropTypes from "prop-types"
import {Col} from "@irati/ui/src/Layout"
import moment from "moment"

import {TeamReport, TeamDate, ReportingTeamState, TeamReportDuration} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import {hangPromise} from "@irati/utils/promise"
import theme from "@irati/ui/src/global/theme"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import {Label, H6, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {InjectIntl, injectIntl, FormattedMessage} from "@irati/utils/lang"
import {Tooltip} from "@irati/ui/src/Tooltip"
import {IconTypes} from "@irati/ui/src/Icon"
import Button from "@irati/ui/src/Button"
import History from "@irati/utils/history"
import adminTracking, {ViewType, Filter} from "@irati-tracking/admin/admin"
import Select from "@irati/ui/src/FormItem/Select"
import Checkbox from "@irati/ui/src/FormItem/Checkbox"
import teamName from "@irati/identity/utils/team/teamName"
import GenericModal from "@irati/ui/src/GenericModal"
import intercom from "@irati/utils/intercom"

import {VIEW_TEAM, VIEW_PEOPLE} from "../../../routes/routeConfig"
import teamSizeRange from "../../../images/teamSizeRange.svg"
import CustomizedShape from "./CustomizedShape"
import CustomizedLabel from "./CustomizedLabel"
import EmptyTeams from "./EmptyTeams"
import Banner from "./Banner"
import Header from "./Header"
import Footer from "./Footer"
import {groupCloseTeams} from "./groupCloseTeams"
import {Quadrant} from "./quadrants"
import {
  Wrapper,
  ClickableCard,
  LabelTooltip,
  Circle,
  YAxisWrapper,
  XAxisWrapper,
  YAxisIcon,
  XAxisIcon,
  TeamSizeIcon,
  FlexRow
} from "./styled"
import {getMedian, getMinAndMax} from "./buildRanges"

interface TimeRangeOption {
  value: string
  label: string
}

interface Props extends InjectIntl {
  source: string
}
interface State {
  initialLoad: Promise<void>
  load: Promise<void>
  showCardTooltip: number
  selectedQuadrant?: Quadrant
  cardTooltip?: any
  reports?: any
  activeMedian: number
  overallMedian: number
  activeMax: number
  activeMin: number
  overallMax: number
  overallMin: number
  enabledUsersMedian?: number
  lowerEnabledUsersMedian?: number
  upperEnabledUsersMedian?: number
  bottomLeftTeams: TeamReport[]
  topLeftTeams: TeamReport[]
  bottomRightTeams: TeamReport[]
  topRightTeams: TeamReport[]
  timeRangeOptions: TimeRangeOption[]
  timeRange?: TeamDate
  showRegions: boolean
}

class Dashboard extends Component<Props, State> {
  static contextTypes = {
    appPath: PropTypes.string.isRequired
  }

  state: State = {
    initialLoad: hangPromise(),
    load: null,
    showCardTooltip: 0,
    selectedQuadrant: undefined,
    activeMedian: 50,
    overallMedian: 50,
    activeMax: 100,
    overallMax: 100,
    activeMin: 0,
    overallMin: 0,
    timeRangeOptions: [],
    timeRange: undefined,
    showRegions: false
  }

  componentDidMount() {
    const initialLoad = this.getTeamReports().then((availableDates) => {
      const [, ...previousMonths] = availableDates
      const timeRangeOptions = previousMonths.map((value, index) => {
        return {
          label:
            index === 0
              ? this.props.formatMessage({id: "admin.components.teamEngagement.select.lastMonth"})
              : `${moment.months(value.month - 1)} '${value.year.toString().substring(2)}`,
          value: `${value.month}-${value.year}`
        }
      })
      this.setState({timeRangeOptions, timeRange: timeRangeOptions[0]})
    })
    this.setState({initialLoad})
  }

  getTeamReports = (timeRange?: TeamDate) => {
    const teamStates = this.state.showRegions ? [ReportingTeamState.Generic, ReportingTeamState.BillingUnit] : [ReportingTeamState.Generic]
    return adminReportingService
      .getTeamReports(authentication.business().uuid, TeamReportDuration.Month, teamStates, timeRange, {fetchPolicy: "network-only"})
      .then((res) => {
        const reportsExcludingTeamsOfOne = res.reports.filter(({enabledUsers}) => enabledUsers > 1)

        const enabledUsersInTeam = reportsExcludingTeamsOfOne.map(({enabledUsers}) => enabledUsers)
        const enabledUsersMedian = getMedian(enabledUsersInTeam)
        const teamsWithLessUsersThanMedian = enabledUsersInTeam.filter((enabledUser) => enabledUser <= enabledUsersMedian)
        const teamsWithMoreUsersThanMedian = enabledUsersInTeam.filter((enabledUser) => enabledUser > enabledUsersMedian)
        const lowerEnabledUsersMedian = getMedian(teamsWithLessUsersThanMedian)
        const upperEnabledUsersMedian = getMedian(teamsWithMoreUsersThanMedian)

        const reports = reportsExcludingTeamsOfOne.map((report: TeamReport) => {
          return {
            ...report,
            engaged: Math.round(report.engaged),
            overall: Math.round(report.overall)
          }
        })
        const activeNumbers = reports.map(({engaged}) => engaged)
        const overallNumbers = reports.map(({overall}) => overall)

        const activeMedian = getMedian(activeNumbers)
        const activeMinAndMax = getMinAndMax(activeNumbers, activeMedian)
        const activeMax = activeMinAndMax.maximum
        const activeMin = activeMinAndMax.minimum

        const overallMedian = getMedian(overallNumbers)
        const overallMinAndMax = getMinAndMax(overallNumbers, overallMedian)
        const overallMax = overallMinAndMax.maximum
        const overallMin = overallMinAndMax.minimum

        const activeDifference = this.getDifference(activeMinAndMax.range)
        const overallDifference = this.getDifference(overallMinAndMax.range)

        const bottomLeftTeams = reports.filter((teams) => teams.overall <= overallMedian && teams.engaged <= activeMedian)
        const topLeftTeams = reports.filter((teams) => teams.overall > overallMedian && teams.engaged <= activeMedian)
        const bottomRightTeams = reports.filter((teams) => teams.overall <= overallMedian && teams.engaged > activeMedian)
        const topRightTeams = reports.filter((teams) => teams.overall > overallMedian && teams.engaged > activeMedian)

        const fullReports = groupCloseTeams(reports, activeMin, activeMax, activeDifference, overallMin, overallMax, overallDifference)

        this.setState({
          reports: fullReports,
          activeMedian,
          overallMedian,
          activeMax,
          overallMax,
          activeMin,
          overallMin,
          enabledUsersMedian,
          lowerEnabledUsersMedian,
          upperEnabledUsersMedian,
          bottomLeftTeams,
          topLeftTeams,
          bottomRightTeams,
          topRightTeams
        })

        return res.availableDates
      })
  }

  handleSetTimeRangeUpdate = (timeRange: string) => {
    adminTracking.reporting({
      source: this.props.source,
      type: "overall_teamAnalysis",
      viewType: timeRange === this.state.timeRangeOptions[0].value ? ViewType.default : ViewType.filtered,
      filter: Filter.date
    })
    const time = timeRange.split("-")
    this.getTeamReports({month: parseInt(time[0], 10), year: parseInt(time[1], 10)})
    this.setState({timeRange})
  }

  getDifference = (range: number) => {
    if (range > 30) {
      return 1
    }
    return 0
  }

  // eslint-disable-next-line no-undef
  handleViewTeamClick = (team: Team) => {
    if (team.teamState === ReportingTeamState.BillingUnit) {
      History.push(
        {
          pathname: VIEW_PEOPLE,
          state: {businessUnitId: team.uuid}
        },
        this.context
      )
    } else {
      History.push(
        {
          pathname: VIEW_TEAM,
          state: {team: team.teamName}
        },
        this.context
      )
    }
  }

  handleCardClick = () => {
    this.setState((prevState) => ({showCardTooltip: prevState.showCardTooltip + 1}))
  }

  updateSelectedQuadrant = (props: ScatterProps) => {
    if (this.state.showCardTooltip > 0) {
      this.setState((prevState) => ({showCardTooltip: prevState.showCardTooltip - 1}))
    }

    const {xValue, yValue} = props || {}
    const {activeMedian, overallMedian} = this.state

    if (xValue && yValue) {
      if (yValue > overallMedian) {
        if (xValue < activeMedian) {
          this.handleTopLeftQuadrantClick()
        } else {
          this.handleTopRightQuadrantClick()
        }
      } else if (xValue < activeMedian) {
        this.handleBottomLeftQuadrantClick()
      } else {
        this.handleBottomRightQuadrantClick()
      }
    }
  }

  handleScatterClick = (props: ScatterProps) => {
    adminTracking.reporting({
      source: this.props.source,
      type: "overall_teamAnalysis",
      viewType: ViewType.filtered,
      filter: Filter.zone,
      action: "viewTeam"
    })
    this.setState({showCardTooltip: 1, cardTooltip: props})
  }

  handleQuadrantsTrackEvent = () => {
    return adminTracking.reporting({
      source: this.props.source,
      type: "overall_teamAnalysis",
      viewType: ViewType.filtered,
      filter: Filter.zones
    })
  }

  handleBottomLeftQuadrantClick = () => {
    const {selectedQuadrant} = this.state
    this.handleQuadrantsTrackEvent()
    if (selectedQuadrant === Quadrant.BottomLeftQuadrant) {
      return this.setState({selectedQuadrant: undefined})
    }
    this.setState({selectedQuadrant: Quadrant.BottomLeftQuadrant})
  }

  handleTopLeftQuadrantClick = () => {
    const {selectedQuadrant} = this.state
    this.handleQuadrantsTrackEvent()
    if (selectedQuadrant === Quadrant.TopLeftQuadrant) {
      return this.setState({selectedQuadrant: undefined})
    }
    this.setState({selectedQuadrant: Quadrant.TopLeftQuadrant})
  }

  handleBottomRightQuadrantClick = () => {
    const {selectedQuadrant} = this.state
    this.handleQuadrantsTrackEvent()
    if (selectedQuadrant === Quadrant.BottomRightQuadrant) {
      return this.setState({selectedQuadrant: undefined})
    }
    this.setState({selectedQuadrant: Quadrant.BottomRightQuadrant})
  }

  handleTopRightQuadrantClick = () => {
    const {selectedQuadrant} = this.state
    this.handleQuadrantsTrackEvent()
    if (selectedQuadrant === Quadrant.TopRightQuadrant) {
      return this.setState({selectedQuadrant: undefined})
    }
    this.setState({selectedQuadrant: Quadrant.TopRightQuadrant})
  }

  handleShowRegions = () => {
    adminTracking.reporting({
      source: this.props.source,
      type: "overall_teamAnalysis",
      viewType: ViewType.filtered,
      filter: Filter.country
    })
    this.setState((prevState: State) => {
      return {showRegions: !prevState.showRegions}
    }, this.getTeamReports)
  }

  handleOpenIntercom = () => {
    intercom.addCustomMessage(this.props.formatMessage({id: "admin.components.teamEngagement.intercomMessage"}))
  }

  render() {
    const {
      initialLoad,
      load,
      selectedQuadrant,
      reports,
      showCardTooltip,
      cardTooltip,
      enabledUsersMedian,
      lowerEnabledUsersMedian,
      upperEnabledUsersMedian,
      activeMedian,
      overallMedian,
      activeMax,
      overallMax,
      activeMin,
      overallMin,
      bottomLeftTeams,
      topLeftTeams,
      bottomRightTeams,
      topRightTeams,
      timeRange,
      timeRangeOptions,
      showRegions
    } = this.state
    const {source, formatMessage} = this.props

    return (
      <Loading load={initialLoad}>
        {!reports || reports?.length <= 2 ? (
          <EmptyTeams />
        ) : (
          <>
            <Header source={source} />
            <Body marginBottom={2} maxWidth="650px">
              <FormattedMessage id="admin.components.teamEngagement.header.text" />
            </Body>
            <Banner
              quadrant={selectedQuadrant}
              onBottomLeftClick={this.handleBottomLeftQuadrantClick}
              onTopLeftClick={this.handleTopLeftQuadrantClick}
              onBottomRightClick={this.handleBottomRightQuadrantClick}
              onTopRightClick={this.handleTopRightQuadrantClick}
            />
            {timeRangeOptions.length > 1 && (
              <FlexRow>
                <Col xs={4}>
                  <Select
                    name="timeRangeTeamEngagement"
                    aria-label="Select Time Period"
                    options={timeRangeOptions}
                    value={timeRange}
                    onChange={this.handleSetTimeRangeUpdate}
                  />
                </Col>
                <Col xs={4}>
                  <Checkbox name="showRegions" checked={showRegions} onChange={this.handleShowRegions}>
                    <FormattedMessage id="admin.components.teamEngagement.countriesCheckbox" />
                  </Checkbox>
                </Col>
              </FlexRow>
            )}
            <Wrapper>
              <Loading load={load}>
                <ResponsiveContainer width="100%" height={500}>
                  <ScatterChart
                    onClick={this.updateSelectedQuadrant}
                    data={reports}
                    margin={{
                      top: 20,
                      right: 80,
                      bottom: 20,
                      left: 20
                    }}>
                    <CartesianGrid stroke="#f5f5f5" />
                    <ReferenceArea
                      x1={activeMedian}
                      x2={activeMax}
                      y1={overallMin}
                      y2={overallMedian}
                      id="adminSection_teamEngagementBottomRightQuadrant"
                      alwaysShow
                      label={
                        <CustomizedLabel
                          x="51%"
                          y={260}
                          value={formatMessage({id: "admin.components.teamEngagement.bottomRightQuadrant.text"})}
                          isFocused={selectedQuadrant === Quadrant.BottomRightQuadrant}
                        />
                      }
                      fill={selectedQuadrant === Quadrant.BottomRightQuadrant ? theme.colors.primary1_50 : "transparent"}
                    />
                    <ReferenceArea
                      x1={activeMedian}
                      x2={activeMax}
                      y1={overallMedian}
                      y2={overallMax}
                      id="adminSection_teamEngagementTopRightQuadrant"
                      alwaysShow
                      label={
                        <CustomizedLabel
                          x="51%"
                          y={40}
                          value={formatMessage({id: "admin.components.teamEngagement.topRightQuadrant.text"})}
                          isFocused={selectedQuadrant === Quadrant.TopRightQuadrant}
                        />
                      }
                      fill={selectedQuadrant === Quadrant.TopRightQuadrant ? theme.colors.primary1_50 : "transparent"}
                    />
                    <ReferenceArea
                      x1={activeMin}
                      x2={activeMedian}
                      y1={overallMin}
                      y2={overallMedian}
                      id="adminSection_teamEngagementBottomLeftQuadrant"
                      alwaysShow
                      label={
                        <CustomizedLabel
                          x={90}
                          y={260}
                          value={formatMessage({id: "admin.components.teamEngagement.bottomLeftQuadrant.text"})}
                          isFocused={selectedQuadrant === Quadrant.BottomLeftQuadrant}
                        />
                      }
                      fill={selectedQuadrant === Quadrant.BottomLeftQuadrant ? theme.colors.primary1_50 : "transparent"}
                    />
                    <ReferenceArea
                      x1={activeMin}
                      x2={activeMedian}
                      y1={overallMedian}
                      y2={overallMax}
                      id="adminSection_teamEngagementTopLeftQuadrant"
                      alwaysShow
                      label={
                        <CustomizedLabel
                          x={90}
                          y={40}
                          value={formatMessage({id: "admin.components.teamEngagement.topLeftQuadrant.text"})}
                          isFocused={selectedQuadrant === Quadrant.TopLeftQuadrant}
                        />
                      }
                      fill={selectedQuadrant === Quadrant.TopLeftQuadrant ? theme.colors.primary1_50 : "transparent"}
                    />
                    <ReferenceLine y={overallMedian} stroke={theme.colors.greys.black} strokeDasharray="3 3" />
                    <ReferenceLine x={activeMedian} stroke={theme.colors.greys.black} strokeDasharray="3 3" />
                    <RechartsTooltip
                      content={
                        showCardTooltip ? (
                          <ClickableCard id="adminSection_teamsGraphDetailsCard" onClick={this.handleCardClick} padding={1}>
                            {cardTooltip?.teamNames ? (
                              <>
                                <Label small weight="bold" display="block" marginBottom={1}>
                                  {cardTooltip?.teamNames.length} {formatMessage({id: "admin.components.teamEngagement.tooltip.teams"})}
                                </Label>
                                {cardTooltip?.teamNames.map((name: string) => {
                                  return (
                                    <Label small display="block" marginBottom={1}>
                                      {name}
                                    </Label>
                                  )
                                })}
                              </>
                            ) : (
                              <>
                                {" "}
                                <Label small weight="bold">
                                  {teamName(cardTooltip)}
                                </Label>
                                <Flex justifyContent="space-between" alignItems="center" margin={[2, 0, 1]}>
                                  <Label small>
                                    <FormattedMessage id="admin.components.teamEngagement.tooltip.people" />
                                  </Label>
                                  <H6>{cardTooltip?.enabledUsers}</H6>
                                </Flex>
                                <Flex justifyContent="space-between" alignItems="center" marginBottom={1}>
                                  <Label small>
                                    <FormattedMessage id="admin.components.teamEngagement.tooltip.activated" />
                                  </Label>
                                  <H6>{cardTooltip?.activeUsers}</H6>
                                </Flex>
                                <Flex justifyContent="space-between" alignItems="center" marginBottom={1}>
                                  <Label small>
                                    <FormattedMessage id="admin.components.teamEngagement.tooltip.manager" />
                                  </Label>
                                  <H6>{cardTooltip?.managers}</H6>
                                </Flex>
                                <Flex justifyContent="center">
                                  <Button
                                    id={
                                      cardTooltip?.teamState === ReportingTeamState.BillingUnit ? "viewCountry_button" : "viewTeam_button"
                                    }
                                    primary
                                    onClick={() => this.handleViewTeamClick(cardTooltip)}
                                    small>
                                    {cardTooltip?.teamState === ReportingTeamState.BillingUnit ? (
                                      <FormattedMessage id="admin.components.teamEngagement.tooltip.countriesButton" />
                                    ) : (
                                      <FormattedMessage id="admin.components.teamEngagement.tooltip.button" />
                                    )}
                                  </Button>
                                </Flex>
                              </>
                            )}
                          </ClickableCard>
                        ) : (
                          (tooltipProps) => {
                            const teamNameFromPayload = tooltipProps?.payload[0]?.payload?.teamName
                            return (
                              <LabelTooltip small marginTop={1} isSingleTeam={teamNameFromPayload}>
                                {teamNameFromPayload && teamNameFromPayload.replace("platform.billingUnit.", "")}
                              </LabelTooltip>
                            )
                          }
                        )
                      }
                      cursor={false}
                      trigger={showCardTooltip ? "click" : undefined}
                    />
                    <XAxis
                      unit="%"
                      type="number"
                      dataKey="engaged"
                      domain={[activeMin, activeMax]}
                      axisLine={false}
                      tickLine={false}
                      dy={10}
                    />
                    <YAxis
                      unit="%"
                      type="number"
                      dataKey="overall"
                      domain={[overallMin, overallMax]}
                      axisLine={false}
                      tickLine={false}
                      dx={-10}
                    />
                    <Scatter
                      onClick={this.handleScatterClick}
                      shape={
                        <CustomizedShape
                          median={enabledUsersMedian}
                          lowerMedian={lowerEnabledUsersMedian}
                          upperMedian={upperEnabledUsersMedian}
                        />
                      }>
                      <LabelList dataKey="teamNames.length" style={{pointerEvents: "none"}} />
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
                <Tooltip tooltipChildren={formatMessage({id: "admin.components.teamEngagement.yaxis.tooltip"})}>
                  <YAxisWrapper>
                    <YAxisIcon icon={IconTypes.circleInformation} large marginRight={0.5} />
                    <Label small>
                      <FormattedMessage id="admin.components.teamEngagement.yaxis.label" />
                    </Label>
                  </YAxisWrapper>
                </Tooltip>
                <Tooltip tooltipChildren={formatMessage({id: "admin.components.teamEngagement.xaxis.tooltip"})}>
                  <XAxisWrapper>
                    <Label small>
                      <FormattedMessage id="admin.components.teamEngagement.xaxis.label" />
                    </Label>
                    <XAxisIcon icon={IconTypes.circleInformation} large marginLeft={0.5} />
                  </XAxisWrapper>
                </Tooltip>
                <Flex marginLeft={4}>
                  <Flex direction="column" width="100px" inline>
                    <Label marginBottom={1}>
                      <FormattedMessage id="admin.components.teamEngagement.legend.teams" />
                    </Label>
                    <Circle />
                  </Flex>
                  <Flex direction="column" width="160px" inline>
                    <Flex>
                      <Label marginBottom={1}>
                        <FormattedMessage id="admin.components.teamEngagement.legend.teamSize" />
                      </Label>
                      <Tooltip tooltipChildren={formatMessage({id: "admin.components.teamEngagement.teamSize.tooltip"})}>
                        <TeamSizeIcon icon={IconTypes.circleInformation} large marginLeft={0.5} />
                      </Tooltip>
                    </Flex>
                    <img src={teamSizeRange} alt="Team size range" />
                    <Flex justifyContent="space-between">
                      <Label small>
                        <FormattedMessage id="admin.components.teamEngagement.legend.smallest" />
                      </Label>
                      <Label small>
                        <FormattedMessage id="admin.components.teamEngagement.legend.largest" />
                      </Label>
                    </Flex>
                  </Flex>
                </Flex>
                {selectedQuadrant && (
                  <Footer
                    quadrant={selectedQuadrant}
                    bottomLeftTeams={bottomLeftTeams}
                    topLeftTeams={topLeftTeams}
                    bottomRightTeams={bottomRightTeams}
                    topRightTeams={topRightTeams}
                    source={source}
                  />
                )}
              </Loading>
            </Wrapper>
          </>
        )}
        <GenericModal
          title={formatMessage({id: "admin.components.teamEngagement.intercomModal.title"})}
          show={!authentication.apps("admin.billingUnit") && showRegions}
          proceedButtonText={formatMessage({id: "admin.components.teamEngagement.intercomModal.button"})}
          onConfirm={this.handleOpenIntercom}
          onHide={() => this.setState({showRegions: false})}>
          <Body marginBottom={6}>
            <FormattedMessage id="admin.components.teamEngagement.intercomModal.body" />
          </Body>
        </GenericModal>
      </Loading>
    )
  }
}

export default injectIntl(Dashboard)
